import { networkApi } from '../infrastructure/api/NetworkApiClient';

export interface ElementSeriesPoint {
  t: string;
  loadingAvg: number | null;
  loadingMax: number | null;
  powerMinKw: number | null;
  powerAvgKw: number | null;
  powerMaxKw: number | null;
  voltageMinPu: number | null;
  voltageAvgPu: number | null;
  voltageMaxPu: number | null;
}

export interface ElementSeriesResponse {
  elementId: string;
  bucket: 'raw' | 'hour' | 'day';
  points: ElementSeriesPoint[];
}

export interface SnapshotRange {
  from?: string;
  to?: string;
  count: number;
}

/** Available powerflow snapshot range of the active zone. */
export async function fetchSnapshotRange(): Promise<SnapshotRange> {
  return networkApi.zoneGet<SnapshotRange>('/powerflow-snapshots/range');
}

/**
 * Per-element time series (loading/power/voltage) from the powerflow snapshot
 * archive. Long windows are bucketed server-side (min/avg/max per hour/day).
 */
export async function fetchElementSeries(
  elementId: string,
  fromIso: string,
  toIso: string
): Promise<ElementSeriesResponse> {
  const params = new URLSearchParams({ elementId, from: fromIso, to: toIso });
  return networkApi.zoneGet<ElementSeriesResponse>(`/powerflow-snapshots/series?${params.toString()}`);
}

export interface MeasuredTrafoSeriesPoint {
  t: string;
  freqMin: number | null;
  freqAvg: number | null;
  freqMax: number | null;
  uMinV: number | null;
  uAvgV: number | null;
  uMaxV: number | null;
  iAvgA: number | null;
  iMaxA: number | null;
}

export interface MeasuredTrafoSeriesResponse {
  elementId: string;
  bucket: 'raw' | 'hour' | 'day';
  points: MeasuredTrafoSeriesPoint[];
}

export interface MeasuredTrafoAt {
  elementId: string;
  measuredAt: string;
  source: string | null;
  frequencyHz: number | null;
  u1V: number | null;
  u2V: number | null;
  u3V: number | null;
  i1A: number | null;
  i2A: number | null;
  i3A: number | null;
  cosPhi: number | null;
}

/**
 * The MEASURED trafo record valid at an instant (newest measurement at or
 * before `tsIso`, at most 30 min old). Null when the station has no
 * measurement for that instant (backend answers 204).
 */
export async function fetchMeasuredTrafoAt(
  elementId: string,
  tsIso: string
): Promise<MeasuredTrafoAt | null> {
  const params = new URLSearchParams({ elementId, ts: tsIso });
  const data = await networkApi.zoneGet<MeasuredTrafoAt | '' | null>(
    `/powerflow-snapshots/measured-trafo-at?${params.toString()}`
  );
  return data && typeof data === 'object' && data.measuredAt ? data : null;
}

/**
 * MEASURED trafo series (frequency, per-phase U/I) from
 * trafo_measurement_history — the measured counterpart to the computed
 * snapshot series.
 */
export async function fetchMeasuredTrafoSeries(
  elementId: string,
  fromIso: string,
  toIso: string
): Promise<MeasuredTrafoSeriesResponse> {
  const params = new URLSearchParams({ elementId, from: fromIso, to: toIso });
  return networkApi.zoneGet<MeasuredTrafoSeriesResponse>(
    `/powerflow-snapshots/measured-trafo-series?${params.toString()}`
  );
}
